/**
 * Authenticated fetch for Elite Pulse report endpoints.
 *
 * The report routes on the flow backend (`/api/ois/report-*` and `/api/ois/intel/bundle`)
 * require a credential. They previously did not, and were served to anyone who knew the
 * URL — including on ports that never passed through the edge proxy.
 *
 * Report calls are spread over half a dozen components, so they go through one helper
 * rather than each remembering to attach headers: a call site that forgets now fails
 * closed and visibly, instead of quietly rendering an empty panel.
 */

import { getApiUrl } from "../config/api";
import { getMmecAuthHeaderFields } from "./authUtils";

/** Raised when the backend rejects the credential, so callers can prompt a re-login rather than show "no data". */
export class ElitePulseAuthError extends Error {
    readonly status: number;

    constructor(status: number, message: string) {
        super(message);
        this.name = "ElitePulseAuthError";
        this.status = status;
    }
}

export interface ElitePulseFetchOptions {
    signal?: AbortSignal;
    /** Prior ETag; when it still matches, the server answers 304 and sends no body. */
    etag?: string;
    headers?: Record<string, string>;
    method?: string;
    body?: BodyInit | null;
}

/**
 * Fetch an Elite Pulse endpoint with the caller's session credentials attached.
 *
 * `path` may be an absolute URL or an API-relative path; relative paths are resolved
 * through `getApiUrl` so backend routing stays in one place.
 */
export const elitePulseFetch = async (
    path: string,
    options: ElitePulseFetchOptions = {}
): Promise<Response> => {
    const url = /^https?:\/\//i.test(path) ? path : getApiUrl(path);

    const headers: Record<string, string> = {
        ...getMmecAuthHeaderFields(),
        ...(options.headers || {}),
    };
    if (options.etag) {
        headers["If-None-Match"] = options.etag;
    }

    const response = await fetch(url, {
        method: options.method || "GET",
        headers,
        body: options.body ?? null,
        signal: options.signal,
    });

    if (response.status === 401 || response.status === 403) {
        throw new ElitePulseAuthError(
            response.status,
            "Your session is not authorised for Elite Pulse reports. Sign in again to continue."
        );
    }

    return response;
};

/** True when a rejected request was an auth failure rather than a missing or malformed report. */
export const isElitePulseAuthError = (error: unknown): error is ElitePulseAuthError =>
    error instanceof ElitePulseAuthError;
